import React from "react";
import { SpinnerGap } from "@phosphor-icons/react";

/**
 * Eric 2026-07-20 (iter162) — Extrait de VideoDetailPage.js (refactor 2500→).
 *
 * Barre de progression visuelle des jobs IA d'arrière-plan (Concepts /
 * Objections / Q&R). Affichée en haut de page tant que `aiJobProgress` est
 * non-null. Purement présentationnel — pas de hooks, pas de state.
 */
export const AIJobProgressBar = ({ aiJobProgress, lang }) => {
  if (!aiJobProgress) return null;

  const jobTitle =
    aiJobProgress.kind === "concepts"
      ? (lang === "fr" ? "Extraction des concepts" : "Concept extraction")
      : aiJobProgress.kind === "objections"
        ? (lang === "fr" ? "Génération des objections" : "Objection generation")
        : (lang === "fr" ? "Génération Q&R" : "Q&A generation");

  const pct = aiJobProgress.total_chunks > 0
    ? Math.min(100, Math.round((aiJobProgress.chunks_processed / aiJobProgress.total_chunks) * 100))
    : null;

  const stepLabel = (() => {
    const s = aiJobProgress.current_step;
    if (!s || s === "running") return null;
    if (s === "init" || s === "starting") return lang === "fr" ? "Initialisation…" : "Starting…";
    if (s === "loading_video" || s === "loading_transcript") return lang === "fr" ? "Chargement du transcript…" : "Loading transcript…";
    if (s === "chunking_done") return lang === "fr" ? "Découpage terminé, génération en cours…" : "Chunking done, generating…";
    if (s === "filtering") return lang === "fr" ? "Filtrage des passages anodins…" : "Filtering low-value chunks…";
    if (s === "persisting") return lang === "fr" ? "Sauvegarde en base…" : "Persisting…";
    if (s === "finalizing") return lang === "fr" ? "Finalisation…" : "Finalizing…";
    if (s === "no_concepts_returned") return lang === "fr" ? "Aucun concept extrait" : "No concepts returned";
    if (s.startsWith("created_")) return lang === "fr" ? `${s.replace("created_", "")} élément(s) créé(s)` : `${s.replace("created_", "")} created`;
    if (s.startsWith("error_")) return lang === "fr" ? "Erreur sur un chunk (poursuit)…" : "Chunk error (continuing)…";
    return s;
  })();

  return (
    <div
      className="sticky top-0 z-30 mb-3 rounded-xl border border-violet-200 bg-gradient-to-r from-violet-50 via-blue-50 to-violet-50 shadow-sm overflow-hidden"
      data-testid="ai-job-progress-bar"
    >
      <div className="px-4 py-2.5 flex items-center gap-3 text-xs">
        <SpinnerGap size={16} className="animate-spin text-violet-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="font-bold text-violet-900 uppercase tracking-wider">{jobTitle}</span>
            <span className="text-violet-700 font-mono shrink-0" data-testid="ai-job-progress-counter">
              {aiJobProgress.chunks_processed || 0}
              {aiJobProgress.total_chunks > 0 && ` / ${aiJobProgress.total_chunks}`}
              {pct !== null && (
                <span className="ml-2 text-violet-500">({pct}%)</span>
              )}
            </span>
          </div>
          <div className="mt-1.5 h-1.5 bg-violet-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                aiJobProgress.total_chunks > 0
                  ? "bg-gradient-to-r from-violet-500 to-blue-500"
                  : "bg-gradient-to-r from-violet-300 to-blue-300 animate-pulse"
              }`}
              style={{
                width: aiJobProgress.total_chunks > 0
                  ? `${Math.min(100, (aiJobProgress.chunks_processed / aiJobProgress.total_chunks) * 100)}%`
                  : "30%",
              }}
              data-testid="ai-job-progress-fill"
            />
          </div>
          {stepLabel && (
            <p className="mt-1 text-[10px] text-violet-600 truncate" data-testid="ai-job-progress-step">
              {stepLabel}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIJobProgressBar;
